import {
  TableRow,
  TableCell,
  Box,
  Checkbox,
  TextField,
  Autocomplete,
  Tooltip,
  Typography,
} from "@mui/material";
import type {
  CreateMaterialCodePayload,
  MaterialSupplierDetail,
} from "@/api/materialSupplierService";
import MaterialCodeCell from "./MaterialCodeCell";
import { isValidDecimalInput } from "@/utils/inputParse";
import SupplierSelectField from "./SupplierSelectField";
import LossParameterField from "./LossParameterField";
import { useMemo, useState, useEffect } from "react";
import { useMaterialCodeLogic } from "@/hooks/useMaterialCodeLogic";
import type { Master } from "@/api/masterService";
import type { FirstTierSupplier } from "@/api/supplierService";

const DecimalInputCell = ({
  col,
  value,
  onChange,
}: {
  col: { parameter: string; name: string };
  value: number;
  onChange: (value: number, name: string) => void;
}) => {
  const [inputVal, setInputVal] = useState(value.toFixed(2));

  useEffect(() => {
    setInputVal(value.toFixed(2));
  }, [value]);

  return (
    <TableCell>
      <TextField
        size="small"
        fullWidth
        placeholder={col.name}
        value={inputVal}
        onChange={(e) => {
          const val = e.target.value;
          if (isValidDecimalInput(val, 2)) {
            setInputVal(val);
          }
        }}
        onBlur={() => {
          const num = inputVal === "" ? 0 : parseFloat(inputVal) || 0;
          setInputVal((num).toFixed(2));
          onChange(num, col.name);
        }}
        sx={{ "& input": { textAlign: "right" } }}
      />
    </TableCell>
  );
};

const OtherCostInputCell = ({
  col,
  value,
  onChange,
}: {
  col: { id: number; name: string };
  value: number;
  onChange: (value: number) => void;
}) => {
  const [inputVal, setInputVal] = useState(value.toFixed(2));

  useEffect(() => {
    setInputVal(value.toFixed(2));
  }, [value]);

  return (
    <TableCell>
      <TextField
        size="small"
        fullWidth
        placeholder={col.name}
        value={inputVal}
        onChange={(e) => {
          const val = e.target.value;
          if (isValidDecimalInput(val, 2)) {
            setInputVal(val);
          }
        }}
        onBlur={() => {
          const num = inputVal === "" ? 0 : parseFloat(inputVal) || 0;
          setInputVal((num).toFixed(2));
          onChange(num);
        }}
        sx={{ "& input": { textAlign: "right" } }}
      />
    </TableCell>
  );
};

const DimensionInputCell = ({
  col,
  value,
  onChange,
}: {
  col: { parameter: string; name: string };
  value: number;
  onChange: (value: number, name: string) => void;
}) => {
  const [inputVal, setInputVal] = useState(String(value));

  useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  return (
    <TableCell key={col.parameter}>
      <TextField
        size="small"
        fullWidth
        placeholder={col.name}
        value={inputVal}
        onChange={(e) => {
          const val = e.target.value;
          if (isValidDecimalInput(val, 2, false)) {
            setInputVal(val);
          }
        }}
        onBlur={() => {
          const num = inputVal === "" ? 0 : parseFloat(inputVal) || 0;
          setInputVal(String(num));
          onChange(num, col.name);
        }}
        sx={{ "& input": { textAlign: "right" } }}
      />
    </TableCell>
  );
};

interface EditRowProps {
  isAdding: boolean;
  isEditing: boolean;
  material: CreateMaterialCodePayload;
  setMaterial: (data: CreateMaterialCodePayload) => void;
  dimensionName: string;
  materialTypes: Array<{ id: number; material_type_name: string }>;
  materialSupplierDetail?: MaterialSupplierDetail;
  secondTierSuppliers?: Array<Master>;
  returnScrapSuppliers?: Array<Master>;
  firstTierSuppliers?: Array<FirstTierSupplier>;
  dimensionColumns: Array<{ parameter: string; name: string }>;
  formulaColumns: Array<{ parameter: string; name: string }>;
  otherColumns: Array<{ id: number; name: string }>;
  materialData?: {
    loss_type?: string;
  };
  isOpenDetail?: boolean;
  isOpenDimension?: boolean;
  isOpenComponentPrice?: boolean;
  isOpenOtherCost?: boolean;
  validationErrors?: Set<string>;
  refPartNo?: string;
}

const EditRow = ({
  isAdding,
  isEditing,
  material,
  setMaterial,
  dimensionName,
  materialTypes,
  materialSupplierDetail,
  secondTierSuppliers,
  returnScrapSuppliers,
  firstTierSuppliers,
  dimensionColumns,
  formulaColumns,
  otherColumns,
  materialData,
  isOpenDetail = true,
  isOpenDimension = true,
  isOpenComponentPrice = true,
  isOpenOtherCost = true,
  validationErrors = new Set(),
  refPartNo = "",
}: EditRowProps) => {
  const materialTypeId = material.material_type_id || 0;
  const roundingDigits =
    materialSupplierDetail?.attributes?.rounding_digits ?? 4;

  const materialSpecs = useMemo(() => {
    const specs = materialSupplierDetail?.material_type_ids?.find(
      (type) => type.id === materialTypeId,
    )?.material_specs;
    return specs || [];
  }, [materialSupplierDetail, materialTypeId]);

  const materialTypeOptions = useMemo(() => {
    return (
      materialTypes?.map((type) => ({
        label: type.material_type_name,
        value: type.id,
      })) || []
    );
  }, [materialTypes]);

  const materialSpecOptions = useMemo(() => {
    return (
      materialSpecs?.map((spec) => ({
        label: spec.spec_name,
        value: spec.material_spec_id,
      })) || []
    );
  }, [materialSpecs]);

  useMaterialCodeLogic({
    material,
    setMaterial,
    materialSupplierDetail,
    dimensionName,
    secondTierSuppliers,
    returnScrapSuppliers,
    firstTierSupplier: firstTierSuppliers,
  });

  const handleFieldChange = (
    field: string,
    value: unknown,
    flaggedReason?: string,
  ) => {
    const flagged_reason = material.flagged_reason
      ? new Set(material.flagged_reason)
      : new Set<string>();
    if (flaggedReason) {
      flagged_reason.add(flaggedReason);
    }

    if (field.startsWith("dimension_parameters.")) {
      const paramKey = field.replace("dimension_parameters.", "");
      setMaterial({
        ...material,
        dimension_parameters: {
          ...material.dimension_parameters,
          [paramKey]: value as number,
        },
        flagged_reason: flagged_reason.size > 0 ? flagged_reason : undefined,
      });
    } else if (field.startsWith("formula_parameters.")) {
      const paramKey = field.replace("formula_parameters.", "");
      setMaterial({
        ...material,
        formula_parameters: {
          ...material.formula_parameters,
          [paramKey]: value as number | boolean,
        },
        flagged_reason: flagged_reason.size > 0 ? flagged_reason : undefined,
      });
    } else {
      setMaterial({
        ...material,
        [field]: value,
        flagged_reason: flagged_reason.size > 0 ? flagged_reason : undefined,
      });
    }
  };

  return (
    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 101,
          width: 70,
          minWidth: 70,
          maxWidth: 70,
          textAlign: "center",
          backgroundColor: "#f5f5f5",
          borderRight: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
        }}
      >
        <Box display="flex" gap={1}>
          <Checkbox disabled checked={isEditing || isAdding} />
        </Box>
      </TableCell>

      <MaterialCodeCell
        materialCode={material.material_code || ""}
        isAdding={isAdding}
        isEditing={isEditing}
      />
      {isOpenDetail && (
        <>
          <TableCell sx={{ whiteSpace: "nowrap", padding: "8px" }}>
            <Autocomplete
              size="small"
              value={
                materialTypeOptions.find(
                  (option) => option.value === material.material_type_id,
                ) || null
              }
              onChange={(_e, newValue) => {
                handleFieldChange("material_type_id", Number(newValue?.value));
              }}
              options={materialTypeOptions}
              slotProps={{
                popper: {
                  style: {
                    width: "fit-content",
                  },
                  modifiers: [
                    {
                      name: "flip",
                      enabled: true,
                    },
                  ],
                },
                listbox: {
                  style: {
                    whiteSpace: "nowrap",
                    width: "fit-content",
                    minWidth: "100%",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={validationErrors.has("material_type_id")}
                  sx={{
                    minWidth: "150px",
                    "& .MuiInputBase-input": {
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "clip",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: validationErrors.has("material_type_id")
                        ? "#d32f2f"
                        : undefined,
                      borderWidth: validationErrors.has("material_type_id")
                        ? 2
                        : 1,
                    },
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      style: { fontSize: "0.875rem" },
                    },
                  }}
                />
              )}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: "nowrap", padding: "8px" }}>
            <Autocomplete
              size="small"
              value={
                materialSpecOptions.find(
                  (option) => option.value === material.material_spec_id,
                ) || null
              }
              onChange={(_e, newValue) => {
                handleFieldChange("material_spec_id", Number(newValue?.value));
              }}
              options={materialSpecOptions}
              slotProps={{
                popper: {
                  style: {
                    width: "fit-content",
                  },
                  modifiers: [
                    {
                      name: "flip",
                      enabled: true,
                    },
                  ],
                },
                listbox: {
                  style: {
                    whiteSpace: "nowrap",
                    width: "fit-content",
                    minWidth: "100%",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={validationErrors.has("material_spec_id")}
                  sx={{
                    minWidth: "150px",
                    "& .MuiInputBase-input": {
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "clip",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: validationErrors.has("material_spec_id")
                        ? "#d32f2f"
                        : undefined,
                      borderWidth: validationErrors.has("material_spec_id")
                        ? 2
                        : 1,
                    },
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      style: { fontSize: "0.875rem" },
                    },
                  }}
                />
              )}
            />
          </TableCell>

          <SupplierSelectField
            value={material.second_tier_supplier_id || ""}
            onChange={(value) =>
              handleFieldChange("second_tier_supplier_id", value)
            }
            suppliers={secondTierSuppliers}
            width={100}
            error={validationErrors.has("second_tier_supplier_id")}
          />

          <SupplierSelectField
            value={material.return_scrap_supplier_id || ""}
            onChange={(value) =>
              handleFieldChange("return_scrap_supplier_id", value)
            }
            suppliers={returnScrapSuppliers}
            width={150}
            error={validationErrors.has("return_scrap_supplier_id")}
          />

          <SupplierSelectField
            value={material.first_tier_supplier_id || ""}
            onChange={(value) => {
              handleFieldChange("first_tier_supplier_id", value);
            }}
            suppliers={firstTierSuppliers}
            width={200}
            error={validationErrors.has("first_tier_supplier_id")}
          />

          <TableCell
            sx={{
              fontSize: "0.9rem",
            }}
          >
            <Tooltip title={refPartNo || ""} placement="top">
              <Typography
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "350px",
                }}
              >
                {refPartNo || ""}
              </Typography>
            </Tooltip>
          </TableCell>
        </>
      )}
      {isOpenDimension &&
        dimensionColumns.map((col) => (
          <DimensionInputCell
            key={col.parameter}
            col={col}
            value={Number(material.dimension_parameters?.[col.parameter] || 0)}
            onChange={(value, name) =>
              handleFieldChange(
                `dimension_parameters.${col.parameter}`,
                value,
                name,
              )
            }
          />
        ))}

      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={Number(material.material_price || 0).toFixed(roundingDigits)}
          disabled
          sx={{ "& input": { textAlign: "right" } }}
        />
      </TableCell>

      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={(
            materialSpecs?.find(
              (spec) => spec.material_spec_id === material.material_spec_id,
            )?.base_price || 0
          ).toFixed(4)}
          disabled
          sx={{ "& input": { textAlign: "right" } }}
        />
      </TableCell>

      {isOpenComponentPrice &&
        formulaColumns.map((col) => {
          const isLossParam =
            col.parameter === "loss1" || col.parameter === "loss2";

          if (isLossParam) {
            const lossType = materialData?.loss_type?.trim().toLowerCase();
            const isPctKey =
              col.parameter === "loss1" ? "is_loss1_pct" : "is_loss2_pct";

            return (
              <LossParameterField
                key={col.parameter}
                name={col.name}
                value={Number(
                  material?.formula_parameters?.[col.parameter] || "0",
                )}
                onChange={(value) =>
                  handleFieldChange(
                    `formula_parameters.${col.parameter}`,
                    value,
                    col.name,
                  )
                }
                onUnitChange={
                  lossType === "mix"
                    ? (isPct) =>
                        handleFieldChange(
                          `formula_parameters.${isPctKey}`,
                          isPct,
                          col.name + " Unit Type",
                        )
                    : undefined
                }
                lossType={lossType}
                isPct={material?.formula_parameters?.[isPctKey] === true}
              />
            );
          }

          const isOtherCost =
            col.parameter.toLocaleLowerCase() === "others_cost";

          if (isOtherCost) {
            return (
              <TableCell key={col.parameter}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={col.name}
                  value={otherColumns
                    .reduce((sum, item) => {
                      const costItem = material.others_cost_detail?.find(
                        (detail) => detail.id === item.id,
                      );
                      return sum + (costItem ? costItem.cost : 0);
                    }, 0)
                    .toFixed(2)}
                  sx={{ "& input": { textAlign: "right" } }}
                  disabled
                />
              </TableCell>
            );
          }

          return (
            <DecimalInputCell
              key={col.parameter}
              col={col}
              value={Number(material?.formula_parameters?.[col.parameter] || 0)}
              onChange={(value, name) =>
                handleFieldChange(
                  `formula_parameters.${col.parameter}`,
                  value,
                  name,
                )
              }
            />
          );
        })}

      {isOpenOtherCost &&
        otherColumns.map((col) => (
          <OtherCostInputCell
            key={col.id}
            col={col}
            value={Number(
              material.others_cost_detail?.find((item) => item.id === col.id)
                ?.cost || 0,
            )}
            onChange={(value) =>
              handleFieldChange(
                `others_cost_detail`,
                [
                  ...(
                    (material.others_cost_detail as Array<{
                      id: number;
                      cost: number;
                    }>) || []
                  ).filter((item) => item.id !== col.id),
                  { id: col.id, cost: value },
                ],
                "Others Cost",
              )
            }
          />
        ))}
    </TableRow>
  );
};

export default EditRow;
